import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPatientLevelListLogic,
  fetchPatientDetailLogic,
  clearPatientDetail,
} from "../../../../store/features/analytics/analyticsSlice";
import { COLORS } from "../mockData";
import PatientDetail from "./PatientDetail";

const ChevronLeft = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRight = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const buildPatientListPayload = (filters = {}, page = 1, pageSize = 10) => {
  const payload = { page, pageSize };

  if (filters.clinicIds?.length) {
    payload.clinic_id = filters.clinicIds.map((id) => Number(id));
  }
  if (filters.patientIds?.length) {
    payload.patient_id = filters.patientIds;
  }
  if (filters.genders?.length) {
    payload.gender = filters.genders;
  }
  if (filters.ageRange) {
    payload.age_range = filters.ageRange;
  }
  if (filters.ticStartDate) {
    payload.time_in_care_start_date = filters.ticStartDate.split("T")[0];
  }
  if (filters.ticEndDate) {
    payload.time_in_care_end_date = filters.ticEndDate.split("T")[0];
  }

  return payload;
};

const getScoreColor = (key, row) => {
  if (key === "phq9Score" && row.isHighRisk?.phq9) return "#EF4444";
  if (key === "gad7Score" && row.isHighRisk?.gad7) return "#EF4444";
  if (key === "phq9Score") return COLORS.phq9;
  if (key === "gad7Score") return COLORS.gad7;
  return COLORS.heading;
};

const PatientTableColumn = ({ columns, rows, selectedPatientId, loadingPatientId, onRowClick }) => (
  <div className="patient-level-table-col">
    <table className="patient-level-table">
      <thead>
        <tr>
          {columns.map((col) => (
            <th key={col.key}>{col.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isLastCol = (col) => col.key === columns[columns.length - 1]?.key;
          const isLoading = row.id === loadingPatientId;
          return (
            <tr
              key={row.id}
              className={`patient-level-row ${selectedPatientId === row.id ? "patient-level-row--selected" : ""}`}
              onClick={() => onRowClick(row)}
              title="Click to view details"
            >
              {columns.map((col) => (
                <td key={col.key}>
                  {col.key === "patientName" ? (
                    <div className="patient-name-cell">
                      <span className="patient-name-text">{row.patientName}</span>
                      {row.dateOfBirth && (
                        <span className="patient-dob-text">{row.dateOfBirth}</span>
                      )}
                    </div>
                  ) : (
                    <div className="patient-score-cell">
                      <span
                        className="patient-score-text"
                        style={{
                          color: getScoreColor(col.key, row),
                          fontWeight: 700,
                        }}
                      >
                        {row[col.key] != null ? row[col.key] : "N/A"}
                      </span>
                      {isLastCol(col) && isLoading && (
                        <span className="patient-row-spinner" />
                      )}
                    </div>
                  )}
                </td>
              ))}
            </tr>
          );
        })}
        {rows.length === 0 && (
          <tr>
            <td colSpan={columns.length} className="patient-level-empty">
              &nbsp;
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const PatientLevelTable = ({ filters = {} }) => {
  const dispatch = useDispatch();
  const {
    patientLevelList,
    patientLevelListLoading,
    patientDetailData,
    patientDetailLoading,
  } = useSelector((state) => state.analytics);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const detailRef = useRef(null);

  const fetchList = useCallback(
    (page, size) => {
      dispatch(fetchPatientLevelListLogic(buildPatientListPayload(filters, page, size)));
    },
    [dispatch, filters]
  );

  useEffect(() => {
    setCurrentPage(1);
    setSelectedPatientId(null);
    dispatch(clearPatientDetail());
    fetchList(1, pageSize);
  }, [filters]);

  useEffect(() => {
    if (!patientDetailLoading && patientDetailData && selectedPatientId && detailRef.current) {
      setTimeout(() => {
        detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [patientDetailLoading, patientDetailData, selectedPatientId]);

  const loadingPatientId = patientDetailLoading ? selectedPatientId : null;

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedPatientId(null);
    dispatch(clearPatientDetail());
    fetchList(page, pageSize);
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    setSelectedPatientId(null);
    dispatch(clearPatientDetail());
    fetchList(1, newSize);
  };

  const handleRowClick = (row) => {
    if (selectedPatientId === row.id) {
      setSelectedPatientId(null);
      dispatch(clearPatientDetail());
      return;
    }

    setSelectedPatientId(row.id);

    const detailPayload = {};
    if (row.source === "precise" || row.precise_patient_id) {
      detailPayload.patient_type = "precise";
      detailPayload.patient_id = row.precise_patient_id;
    } else {
      detailPayload.patient_type = "healthie";
      detailPayload.healthie_patient_id = row.healthie_patient_id;
    }

    if (filters.startDate) {
      detailPayload.start_date = filters.startDate.split("T")[0];
    }
    if (filters.endDate) {
      detailPayload.end_date = filters.endDate.split("T")[0];
    }

    dispatch(fetchPatientDetailLogic(detailPayload));
  };

  const rows = patientLevelList?.rows || [];
  const columns = patientLevelList?.columns || [
    { key: "patientName", label: "Patient Name" },
    { key: "phq9Score", label: "PHQ-9 Score" },
    { key: "gad7Score", label: "GAD-7 Score" },
  ];
  const pagination = patientLevelList?.pagination || {};
  const totalPages = pagination.totalPages || 1;
  const totalCount = pagination.totalCount || 0;

  const half = Math.ceil(rows.length / 2);
  const leftRows = rows.slice(0, half);
  const rightRows = rows.slice(half);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="patient-level-container">
      <div className="patient-level-table-wrapper">
        {patientLevelListLoading && (
          <div className="patient-level-loading-overlay">
            <div className="analytics-loading-spinner" />
            <span className="analytics-loading-text">Loading patients...</span>
          </div>
        )}
        {rows.length > 0 ? (
          <div className="patient-level-two-col">
            <PatientTableColumn
              columns={columns}
              rows={leftRows}
              selectedPatientId={selectedPatientId}
              loadingPatientId={loadingPatientId}
              onRowClick={handleRowClick}
            />
            <PatientTableColumn
              columns={columns}
              rows={rightRows}
              selectedPatientId={selectedPatientId}
              loadingPatientId={loadingPatientId}
              onRowClick={handleRowClick}
            />
          </div>
        ) : (
          !patientLevelListLoading && (
            <div className="patient-level-empty-state">No patients found</div>
          )
        )}
      </div>

      {totalCount > 0 && (
        <div className="patient-level-pagination-wrapper">
          <div className="patient-level-pagination-row">
            <div className="patient-level-pages">
              <button
                className="patient-level-page-arrow"
                disabled={currentPage <= 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                <ChevronLeft />
              </button>
              {getPageNumbers().map((page) => (
                <button
                  key={page}
                  className={`patient-level-page-btn ${page === currentPage ? "patient-level-page-btn--active" : ""}`}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              ))}
              <button
                className="patient-level-page-arrow"
                disabled={currentPage >= totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                <ChevronRight />
              </button>
            </div>
            <div className="patient-level-page-size">
              <span>Rows per page</span>
              <select value={pageSize} onChange={handlePageSizeChange}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={75}>75</option>
                <option value={100}>100</option>
                <option value={150}>150</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
            </div>
          </div>
          <div className="patient-level-pagination-info">
            Showing {pagination.from || 1} to {pagination.to || rows.length} of {totalCount} Patients
          </div>
        </div>
      )}

      {selectedPatientId && (
        <div ref={detailRef}>
          <PatientDetail
            data={patientDetailData}
            loading={patientDetailLoading}
          />
        </div>
      )}
    </div>
  );
};

export default PatientLevelTable;
